import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw,
  Database,
  Server,
  Zap,
  Users
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { errorHandlingService } from '@/services/ErrorHandlingService';

interface HealthMetrics {
  database: 'healthy' | 'degraded' | 'down';
  auth: 'healthy' | 'degraded' | 'down';
  edgeFunctions: 'healthy' | 'degraded' | 'down';
  contentGeneration: 'healthy' | 'degraded' | 'down';
  lastChecked: Date;
}

const SystemHealthMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<HealthMetrics>({
    database: 'healthy',
    auth: 'healthy',
    edgeFunctions: 'healthy',
    contentGeneration: 'healthy',
    lastChecked: new Date()
  });
  const [isChecking, setIsChecking] = useState(false);
  const [recentErrors, setRecentErrors] = useState<any[]>([]);

  const checkSystemHealth = async () => {
    setIsChecking(true);
    const newMetrics: HealthMetrics = {
      database: 'healthy',
      auth: 'healthy',
      edgeFunctions: 'healthy',
      contentGeneration: 'healthy',
      lastChecked: new Date()
    };

    try {
      const { error: dbError } = await supabase.from('courses').select('id').limit(1);
      newMetrics.database = dbError ? 'down' : 'healthy';

      const { data: session } = await supabase.auth.getSession();
      newMetrics.auth = session ? 'healthy' : 'degraded';

      const { data: operations } = await supabase
        .from('practice_generation_operations')
        .select('status')
        .eq('status', 'failed')
        .gte('created_at', new Date(Date.now() - 3600000).toISOString());
      
      newMetrics.contentGeneration = (operations?.length || 0) > 5 ? 'degraded' : 'healthy';
      setMetrics(newMetrics);
      setRecentErrors(errorHandlingService.getRecentErrors(5));
    } catch (error) {
      console.error('Health check failed:', error);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkSystemHealth();
    const interval = setInterval(checkSystemHealth, 60000);
    return () => clearInterval(interval);
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Healthy</Badge>;
      case 'degraded':
        return <Badge variant="outline" className="border-yellow-500 text-yellow-700"><AlertTriangle className="h-3 w-3 mr-1" />Degraded</Badge>;
      case 'down':
        return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Down</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            System Health Monitor
          </CardTitle>
          <Button variant="outline" size="sm" onClick={checkSystemHealth} disabled={isChecking}>
            <RefreshCw className={`h-4 w-4 ${isChecking ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Database className="h-4 w-4" />Database
            </div>
            {getStatusBadge(metrics.database)}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />Authentication
            </div>
            {getStatusBadge(metrics.auth)}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Server className="h-4 w-4" />Edge Functions
            </div>
            {getStatusBadge(metrics.edgeFunctions)}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Zap className="h-4 w-4" />Content Gen
            </div>
            {getStatusBadge(metrics.contentGeneration)}
          </div>
        </div>

        {recentErrors.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Recent Errors ({recentErrors.length})
            </h4>
            <div className="space-y-1 text-xs">
              {recentErrors.map((err, idx) => (
                <div key={idx} className="p-2 bg-muted/50 rounded">
                  <span className="font-mono">[{err.context.category}]</span>
                  {' '}{err.error.message}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          Last checked: {metrics.lastChecked.toLocaleTimeString()}
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemHealthMonitor;